import { ProductReport } from '@/interfaces/ProductReport';
import { RangeInterface } from '@/interfaces/RangeInterface';
import { DynamicObject } from '@/interfaces/DynamicObject';
import { FilterData } from '@/interfaces/FilterData';

const filterableAttributes = ['valuationCause', 'usdRevenues', 'valuationYear', 'brandCountry'];

export class FilterService {
    private productReports: ProductReport[];

    constructor(productReports: ProductReport[]) {
        this.productReports = productReports;
    }

    public getFilterOptions() {
        let valuationCauses: DynamicObject = {};
        let revenueRange: RangeInterface = {maxValue: 0, minValue: 0};
        let revenueTypes: DynamicObject = {};
        let years: RangeInterface = {maxValue: 0, minValue: 0};
        let brandCountries: DynamicObject = {};

        if (!this.productReports.length) {
            return null;
        }
        valuationCauses = this.extractValuationCauses();
        revenueRange = this.extractRevenues();
        revenueTypes = this.extractRevenueTypes();
        years = this.extractYears();
        brandCountries = this.extractBrandCountries();
        return {
            valuationCauses,
            revenueRange,
            revenueTypes,
            years,
            brandCountries,
        };
    }

    public filterProductReports(filterData: FilterData) {
        return new Promise((resolve) => {
            const filteredProductReports = this.productReports.filter((productReport: ProductReport) => {
                const reportItem = productReport.brand ? productReport.brand : productReport;
                if (!filterData.valuationCauses.includes(reportItem.valuationCause.name)) {
                    return false;
                }
                if (Array.isArray(filterData.revenueRange) && reportItem.usdRevenues) {
                    const usdRevenuesValue = Math.round(parseFloat(reportItem.usdRevenues.replace(',', '.')));
                    if (isNaN(usdRevenuesValue) && !filterData.withoutRevenueRecords) {
                        return false;
                    }
                    if (!isNaN(usdRevenuesValue) && !(filterData.revenueRange[0] <= usdRevenuesValue &&
                        filterData.revenueRange[1] >= usdRevenuesValue)) {
                        return false;
                    }
                }
                if (reportItem.revenueType && !filterData.revenueTypes.includes(reportItem.revenueType.name)) {
                    return false;
                }
                if (Array.isArray(filterData.years)) {
                    if (!(filterData.years[0] <= reportItem.valuationYear &&
                        filterData.years[1] >= reportItem.valuationYear)) {
                        return false;
                    }
                }
                if (!filterData.brandCountries.includes(reportItem.brandCountry.name)) {
                    return false;
                }
                return true;
            });
            resolve(filteredProductReports);
        });
    }

    private extractValuationCauses(): DynamicObject {
        const valuationCauses: DynamicObject = {};
        this.productReports.forEach((productReport: ProductReport) => {
            const reportItem = productReport.brand ? productReport.brand : productReport;
            if (valuationCauses[reportItem.valuationCause.name] === undefined) {
                valuationCauses[reportItem.valuationCause.name] = 1;
            } else {
                valuationCauses[reportItem.valuationCause.name]++;
            }
        });
        return valuationCauses;
    }

    private extractRevenues(): RangeInterface {
        const revenues = this.productReports.filter((productReport: ProductReport) => {
            const reportItem = productReport.brand ? productReport.brand : productReport;
            return !isNaN(parseFloat(reportItem.usdRevenues));
        }).map((productReport: ProductReport) => {
            const reportItem = productReport.brand ? productReport.brand : productReport;
            return parseFloat(reportItem.usdRevenues.replace(',', '.'));
        });
        return {
            maxValue: Math.round(Math.max(...revenues)),
            minValue: Math.round(Math.min(...revenues)),
        };
    }

    private extractRevenueTypes(): DynamicObject {
        const revenueTypes: DynamicObject = {};
        this.productReports.forEach((productReport: ProductReport) => {
            const reportItem = productReport.brand ? productReport.brand : productReport;
            if (reportItem.revenueType) {
                if (revenueTypes[reportItem.revenueType.name] === undefined) {
                    revenueTypes[reportItem.revenueType.name] = 1;
                } else {
                    revenueTypes[reportItem.revenueType.name]++;
                }
            }
        });
        return revenueTypes;
    }

    private extractYears(): RangeInterface {
        const years = this.productReports.map((productReport: ProductReport) => {
            const reportItem = productReport.brand ? productReport.brand : productReport;
            return reportItem.valuationYear;
        });
        return {
            maxValue: Math.max(...years),
            minValue: Math.min(...years),
        };
    }

    private extractBrandCountries(): DynamicObject {
        const brandCountries: DynamicObject = {};
        this.productReports.forEach((productReport: ProductReport) => {
            const reportItem = productReport.brand ? productReport.brand : productReport;
            if (brandCountries[reportItem.brandCountry.name] === undefined) {
                brandCountries[reportItem.brandCountry.name] = 1;
            } else {
                brandCountries[reportItem.brandCountry.name]++;
            }
        });
        const ordered: DynamicObject = {};
        Object.keys(brandCountries).sort().forEach((key: string) => {
            ordered[key] = brandCountries[key];
        });

        return ordered;
    }
}
